import Sticky from 'components/Sticky/Sticky';
import useBodyClass from 'helpers/hooks/useBodyClass';
import { CreditContainer } from 'modules/cm/Credit/containers';
import { RepaymentStrategyContainer } from 'modules/cm/RepaymentStrategy/containers';
import { TaskListContainer } from 'modules/cm/Tasks/containers';
import { selectors as tasksSelectors } from 'modules/cm/Tasks/store';
import { VulnerabilitiesContainer } from 'modules/cm/Vulnerabilities/containers';
import { FIELDS } from 'modules/cm/fields/types';
import useError from 'modules/cm/hooks/useCaseStatus';
import setCasePepperErrorsUseCase from 'modules/cm/store/sharedActions/setCasePepperError';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import UWModal from '../../components/UWModal/UWModal';
import ErrorModalContainer from '../../containers/ErrorModalContainer';
import ApplicantRouteWrapper from './ApplicantRouteWrapper';
import { ApplicantRouteAPI } from './ApplicantRouteWrapper.types';
import styles from './CaseDetailsPage.module.scss';
import { CASE_STATUS, Props } from './CaseDetailsPage.types';
import PepperModal from './Pepper/PepperModal';
import { closePepperContext, openPepperContext } from './Pepper/store/useCases';
import useIsPepperSend from './hooks/useIsPepperSend';
import useSaveButtonClicked from './hooks/useSaveButtonClicked';
import { Header, Nav, UnderwritingSummary } from './partials';
import { Banner } from './partials/Header/Header.types';
import * as subPages from './subPages';
import { Rationale } from './subPages';

const CaseDetailsPage: React.FC<Props> = ({
  isDuringSave,
  onTabChange,
  caseDetails,
  initialCaseDetails,
  url,
  onCaseRefresh,
}) => {
  useBodyClass(...styles['new-cm'].split(' '));
  const dispatch = useDispatch();

  const { onButtonClick } = useSaveButtonClicked();

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [successBanner, setSuccessBanner] = useState<Banner>({ isVisible: false });
  const isSendSuccess = useSelector((state: any) => {
    return state.cmPepper.send.isSuccess;
  });
  const usePepperUploadApproved = () => {
    return useSelector((state: any) => state.currentCase.data.case.loanDetails.isPepperUploadApproved);
  };

  const caseStatus = useError();

  const [pepperUploadCheckboxValue] = useState(usePepperUploadApproved());
  const [isSendToPepperDisable, setIsSendToPepperDisable] = useState<boolean>(
    caseStatus === CASE_STATUS.CASE_COMPLETED && pepperUploadCheckboxValue && !isSendSuccess ? false : true,
  );
  const isPepperSend = useIsPepperSend();

  const onSaveClick = () => {
    onButtonClick();
  };

  const checkPepperAvailable = useCallback(() => {
    if (caseStatus === CASE_STATUS.CASE_COMPLETED && pepperUploadCheckboxValue && !isPepperSend) {
      setIsSendToPepperDisable(false);
    } else if (caseStatus !== CASE_STATUS.CASE_COMPLETED || isPepperSend) {
      setIsSendToPepperDisable(true);
    }
  }, [pepperUploadCheckboxValue, caseStatus, isPepperSend]);
  const todoTasksCount = useSelector(tasksSelectors.getTodoTasks).length;
  useEffect(() => {
    checkPepperAvailable();
  }, [caseStatus, checkPepperAvailable]);

  useEffect(() => {
    if (isSendSuccess) {
      setSuccessBanner({ isVisible: true, content: 'Upload successful.' });
      setIsSendToPepperDisable(true);
      setTimeout(() => {
        setSuccessBanner({ isVisible: false });
      }, 10000);
    }
  }, [isSendSuccess]);

  useEffect(() => {
    if (isSendSuccess) {
      setIsSendToPepperDisable(true);
    }
    dispatch(setCasePepperErrorsUseCase(null));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendSuccess]);

  return (
    <>
      <div key={'caseDetailsPage'} className={styles.wrapper}>
        <Sticky top={0}>
          <Header
            caseDetails={caseDetails}
            onPresentButtonClick={setModalVisible.bind(null, true)}
            onPepperButtonClick={dispatch.bind(null, openPepperContext())}
            onRefreshCaseClick={onCaseRefresh}
            onSaveClick={onSaveClick}
            banner={successBanner}
            handleCloseBanner={setSuccessBanner.bind(null, { isVisible: false })}
            isDuringSave={isDuringSave}
          />
        </Sticky>
        <div className={styles.main}>
          <aside>
            <Sticky top={142}>
              <Nav
                applicants={caseDetails.case.applicants}
                resources={caseDetails.resources}
                url={url}
                onNavClick={onTabChange}
                todoTasksCount={todoTasksCount}
              />
            </Sticky>
          </aside>
          <div className="content">
            <Switch>
              <Route path={`${url}/tasklist`}>
                <TaskListContainer />
              </Route>
              <Route path={`${url}/`} exact={true}>
                <subPages.Summary
                  modulePrefix={'update.'}
                  data={caseDetails.case.loanDetails}
                  initialData={initialCaseDetails.case.loanDetails}
                  caseId={caseDetails.case.identifier}
                  resources={caseDetails.resources}
                  onStatusChange={onCaseRefresh}
                  onProductChange={onTabChange}
                  onActionDone={(message: string) =>
                    setSuccessBanner({
                      isVisible: true,
                      content: message,
                    })
                  }
                />
              </Route>

              <Route path={`${url}/repayment-strategy`} exact={true}>
                <RepaymentStrategyContainer caseId={caseDetails.case.identifier} resources={caseDetails.resources} />
              </Route>

              <Route path={[`${url}/applicant/:slug`, `${url}/applicant/:slug/personal-details`]} exact={true}>
                <ApplicantRouteWrapper applicantIds={caseDetails.case.applicants.map((a) => a.id)}>
                  {(api: ApplicantRouteAPI) => {
                    const applicantIndex = api.applicantNumber - 1;

                    return (
                      <subPages.Applicant.PersonalDetails
                        modulePrefix={`${FIELDS.APPLICANTS}.${applicantIndex}.`}
                        applicantIndex={applicantIndex}
                        applicantId={api.applicantId}
                        data={caseDetails.case.applicants[api.applicantNumber - 1]}
                        initialData={initialCaseDetails.case.applicants[api.applicantNumber - 1]}
                        caseId={caseDetails.case.identifier}
                        resources={caseDetails.resources}
                      />
                    );
                  }}
                </ApplicantRouteWrapper>
              </Route>

              <Route path={[`${url}/applicant/:slug/vulnerabilities`]} exact={true}>
                <ApplicantRouteWrapper applicantIds={caseDetails.case.applicants.map((a) => a.id)}>
                  {(api: ApplicantRouteAPI) => {
                    return (
                      <VulnerabilitiesContainer
                        applicantIndex={(api.applicantNumber - 1) as 0 | 1}
                        applicantId={api.applicantId}
                        caseId={caseDetails.case.identifier}
                        resources={caseDetails.resources}
                      />
                    );
                  }}
                </ApplicantRouteWrapper>
              </Route>

              <Route path={`${url}/applicant/:slug/employment`}>
                <ApplicantRouteWrapper applicantIds={caseDetails.case.applicants.map((a) => a.id)}>
                  {(api: ApplicantRouteAPI) => {
                    const applicantIndex = api.applicantNumber - 1;

                    return (
                      <subPages.Applicant.Employment
                        modulePrefix={`${FIELDS.APPLICANTS}.${applicantIndex}.`}
                        applicantIndex={api.applicantNumber - 1}
                        applicantId={api.applicantId}
                        data={caseDetails.case.applicants[api.applicantNumber - 1]}
                        initialData={initialCaseDetails.case.applicants[api.applicantNumber - 1]}
                        caseId={caseDetails.case.identifier}
                        resources={caseDetails.resources}
                      />
                    );
                  }}
                </ApplicantRouteWrapper>
              </Route>

              <Route path={`${url}/applicant/:slug/assets`}>
                <ApplicantRouteWrapper applicantIds={caseDetails.case.applicants.map((a) => a.id)}>
                  {(api: ApplicantRouteAPI) => {
                    const applicantIndex = api.applicantNumber - 1;
                    return (
                      <subPages.Applicant.Assets
                        modulePrefix={`${FIELDS.APPLICANTS}.${applicantIndex}.`}
                        applicantIndex={api.applicantNumber - 1}
                        applicantId={api.applicantId}
                        data={caseDetails.case.applicants[applicantIndex][FIELDS.ASSETS_PREFIX]}
                        initialData={initialCaseDetails.case.applicants[applicantIndex][FIELDS.ASSETS_PREFIX]}
                        caseId={caseDetails.case.identifier}
                        resources={caseDetails.resources}
                        url={url}
                      />
                    );
                  }}
                </ApplicantRouteWrapper>
              </Route>

              <Route path={`${url}/applicant/:slug/expenditure`}>
                <ApplicantRouteWrapper applicantIds={caseDetails.case.applicants.map((a) => a.id)}>
                  {(api: ApplicantRouteAPI) => {
                    const applicantIndex = api.applicantNumber - 1;
                    return caseDetails.case.applicants.length && caseDetails.case.loanDetails ? (
                      <subPages.Applicant.Expenditure
                        modulePrefix={`${FIELDS.APPLICANTS}.${applicantIndex}.`}
                        applicantId={api.applicantId}
                        applicantIndex={applicantIndex}
                        applicantData={caseDetails.case.applicants[applicantIndex]}
                        applicantInitialData={initialCaseDetails.case.applicants[applicantIndex]}
                        loanDetailsData={caseDetails.case.loanDetails}
                        loanDetailsInitialData={initialCaseDetails.case.loanDetails}
                        caseId={caseDetails.case.identifier}
                        resources={caseDetails.resources}
                        url={url}
                        numberOfApplicants={caseDetails.case.applicants.length}
                      />
                    ) : null;
                  }}
                </ApplicantRouteWrapper>
              </Route>

              <Route path={`${url}/applicant/:slug/pensions`}>
                <ApplicantRouteWrapper applicantIds={caseDetails.case.applicants.map((a) => a.id)}>
                  {(api: ApplicantRouteAPI) => {
                    const applicantIndex = api.applicantNumber - 1;

                    return (
                      <subPages.Applicant.Pensions
                        modulePrefix={`${FIELDS.APPLICANTS}.${applicantIndex}.`}
                        applicantId={api.applicantId}
                        applicantIndex={api.applicantNumber - 1}
                        data={caseDetails.case.applicants[api.applicantNumber - 1]}
                        initialData={initialCaseDetails.case.applicants[api.applicantNumber - 1]}
                        caseId={caseDetails.case.identifier}
                        resources={caseDetails.resources}
                        url={url}
                        numberOfApplicants={caseDetails.case.applicants.length}
                        onTabChange={onTabChange}
                      />
                    );
                  }}
                </ApplicantRouteWrapper>
              </Route>

              <Route path={`${url}/applicant/:slug/income`}>
                <ApplicantRouteWrapper applicantIds={caseDetails.case.applicants.map((a) => a.id)}>
                  {(api: ApplicantRouteAPI) => {
                    const applicantData = caseDetails.case.applicants[api.applicantNumber - 1];
                    const applicantIndex = api.applicantNumber - 1;

                    return (
                      <subPages.Applicant.Income
                        applicantIndex={api.applicantNumber - 1}
                        modulePrefix={`${FIELDS.APPLICANTS}.${applicantIndex}.`}
                        applicantId={api.applicantId}
                        data={applicantData}
                        initialData={initialCaseDetails.case.applicants[api.applicantNumber - 1]}
                        caseId={caseDetails.case.identifier}
                        resources={caseDetails.resources}
                        url={url}
                        numberOfApplicants={caseDetails.case.applicants.length}
                        onTabChange={onTabChange}
                      />
                    );
                  }}
                </ApplicantRouteWrapper>
              </Route>

              <Route path={`${url}/property`}>
                <subPages.Property
                  modulePrefix={'property.'}
                  data={caseDetails.case.property}
                  initialData={initialCaseDetails.case.property}
                  caseId={caseDetails.case.identifier}
                  resources={caseDetails.resources}
                />
              </Route>

              <Route path={`${url}/admin`}>
                <subPages.Admin
                  modulePrefix={'contact.'}
                  data={caseDetails.case.contact}
                  initialData={initialCaseDetails.case.contact}
                  caseId={caseDetails.case.identifier}
                  resources={caseDetails.resources}
                />
              </Route>

              <Route path={`${url}/life-assurance`}>
                <subPages.LifeAssurance
                  data={caseDetails.case[FIELDS.ASSURANCE_POLICIES_PREFIX]}
                  initialData={initialCaseDetails.case[FIELDS.ASSURANCE_POLICIES_PREFIX]}
                  caseId={caseDetails.case.identifier}
                  resources={caseDetails.resources}
                />
              </Route>

              <Route path={`${url}/documents`}>
                <subPages.Documents
                  caseId={caseDetails.case.identifier}
                  offerPackUrl={caseDetails.case.offerPackUrl}
                  preOfferEsisUrl={caseDetails.case.preOfferEsisUrl}
                />
              </Route>

              <Route path={`${url}/credit`}>
                <CreditContainer caseId={caseDetails.case.identifier} />
              </Route>
            </Switch>
            <div className={styles.floatingInModal}>
              <Rationale data={caseDetails.case.rationale} isEditable={true} isReadOnly={false} />
            </div>
          </div>
        </div>
      </div>
      {modalVisible && (
        <UWModal isOpen={true} title="Underwriting form" onCloseButtonClick={setModalVisible.bind(null, false)}>
          <UnderwritingSummary />
          <div className={styles.floatingInModal}>
            <Rationale data={caseDetails.case.rationale} isEditable={false} isReadOnly={true} />
          </div>
        </UWModal>
      )}
      <PepperModal
        onClose={dispatch.bind(null, closePepperContext())}
        isDisabledSendToPepperButton={isSendToPepperDisable}
      />
      <ErrorModalContainer />
    </>
  );
};

export default CaseDetailsPage;
